import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { VisualizedDataType } from '../../models/Diary';
import { DataVisualizationService, DataPoint, QuestionMetaMap } from '../../providers/data-visualization/data-visualization.service';
import { DataCategory } from '../data-category-chips/data-category-chips.component';
import { FilterOptions } from '../data-visualization-filters/data-visualization-filters.component';

@Component({
  selector: 'app-data-visualization-wrapper',
  templateUrl: './data-visualization-wrapper.component.html',
  styleUrls: ['./data-visualization-wrapper.component.scss'],
  standalone: false,
})
export class DataVisualizationWrapperComponent implements OnChanges {
  @Input() answersheets: any[] = [];
  @Input() visualizedDataTypes?: VisualizedDataType[];
  @Input({ required: true }) elementMeta!: QuestionMetaMap;
  public sourceOptions: { id: string; label: string }[] = [];

  public availableCategories: DataCategory[] = [];
  public selectedCategoryIds: string[] = [];
  public selectedCategories: DataCategory[] = [];
  public chartData: DataPoint[] = [];

  public currentFilters: FilterOptions = { sourceId: 'all' };

  private colorSlots: [string | null, string | null] = [null, null];

  get emptyReason(): string | null {
    if (this.availableCategories.length === 0 && Array.isArray(this.visualizedDataTypes))
      return 'DATA_VISUALIZATION.NOT_CONFIGURED';
    if ((this.answersheets?.length ?? 0) < 2)
      return 'DATA_VISUALIZATION.NOT_ENOUGH_ENTRIES';
    return null;
  }

  constructor(private dataVisService: DataVisualizationService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['answersheets'] || changes['visualizedDataTypes'] || changes['elementMeta']) && this.answersheets) {
      const raw = this.dataVisService.extractAvailableCategories(this.answersheets, this.visualizedDataTypes, this.elementMeta);
      raw.sort((a, b) => {
        const aNum = Number(a.id);
        const bNum = Number(b.id);
        if (isNaN(aNum) && isNaN(bNum)) return 0;
        if (isNaN(aNum)) return 1;
        if (isNaN(bNum)) return -1;
        return aNum - bNum;
      });
      this.availableCategories = raw;

      const hasSensorData = this.answersheets.some(sheet => sheet.sensor_data?.pedometer?.status === 'success');
      if (hasSensorData) {
        this.sourceOptions = [
          { id: 'all', label: 'DATA_VISUALIZATION.ALL_SOURCES' },
          { id: 'diary', label: 'DATA_VISUALIZATION.SOURCE_DIARY' },
          { id: 'sensor', label: 'DATA_VISUALIZATION.SOURCE_SENSOR' }
        ];
      } else {
        // hide options if no sensor data is available
        this.sourceOptions = [];
        this.currentFilters.sourceId = 'diary';
      }

      // Auto-set the date filters to map exactly to the earliest and latest data points available
      if (this.answersheets.length > 0 && !this.currentFilters.startDate) {
        const timestamps = this.answersheets.map(sheet => sheet.collected_at).sort((a, b) => a - b);
        this.currentFilters.startDate = new Date(timestamps[0]).toISOString();
        this.currentFilters.endDate = new Date(timestamps[timestamps.length - 1]).toISOString();
      }

      // Auto-select first few categories if none selected
      if (this.selectedCategoryIds.length === 0 && this.availableCategories.length > 0) {
        this.selectedCategoryIds = this.availableCategories.slice(0, 2).map(c => c.id);
      }
      this.updateSelectedCategories();
      this.updateChartData();
    }
  }

  public onFiltersChanged(filters: FilterOptions): void {
    this.currentFilters = filters;
    this.updateChartData();
  }

  public onCategoriesChanged(selectedIds: string[]): void {
    this.selectedCategoryIds = selectedIds;
    this.updateSelectedCategories();
    this.updateChartData();
  }

  private updateSelectedCategories(): void {
    const colors = ['#e6821e', '#1e5fa8'];
    this.colorSlots = this.colorSlots.map(id => this.selectedCategoryIds.includes(id!) ? id : null) as [string|null, string|null];
    for (const id of this.selectedCategoryIds)
      if (!this.colorSlots.includes(id))
        this.colorSlots[this.colorSlots[0] == null ? 0 : 1] = id;
    this.availableCategories = this.availableCategories.map(c => ({ ...c, color: colors[this.colorSlots.indexOf(c.id)] }));
    this.selectedCategories = this.colorSlots
      .map(id => this.availableCategories.find(c => c.id === id))
      .filter((c): c is DataCategory => c != null);
  }

  private updateChartData(): void {
    if (!this.answersheets?.length || !this.selectedCategoryIds.length) {
      this.chartData = [];
      return;
    }

    const { startDate, endDate, sourceId } = this.currentFilters;
    const start = startDate ? new Date(startDate).getTime() : undefined;
    const end = endDate ? new Date(endDate).setHours(23, 59, 59, 999) : undefined;

    const points = this.dataVisService.extractDataPoints(this.answersheets, this.selectedCategoryIds, start, end, this.elementMeta);
    this.chartData = (sourceId && sourceId !== 'all') ? points.filter(p => p.source === sourceId) : points;
  }
}
